import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import LeadForm from "./LeadForm";
import { validateLeadForm } from "../../utils/validation";
import { isValidNumberInput, isValidTextInput } from "../../utils/Utils";
import { fetchCountries } from "../../redux/Slices/locationSlice";
import { postMultipleLeadsThunk } from "../../features/Lead/leadsSlice";
import ToastNotification from "../../components/Common/ToastNotification";
import {
  setToastForAddLead,
  resetToastConfig,
} from "../../features/Lead/leadsSlice";
import Loader from "../../components/Common/Loader";
import { languages } from "../../utils/Constant/Constant";
import appInsights from "../../AppInsights";

const AddNewLead = () => {
  const { trans } = useCustomTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { profileData } = useSelector((state) => state.profile);

  const { toastConfig, loading } = useSelector((state) => state.leads);
  const { countries } = useSelector((state) => state.location);

  const [forms, setForms] = useState([
    {
      id: Date.now(),
      formData: {
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        language: "",
        country: "",
        note: "",
      },
      errors: {
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
      },
    },
  ]);

  // App Insights- Page View
  useEffect(() => {
    appInsights.trackPageView({
      name: "Add New Lead Page",
      properties: {
        coachId: profileData?.coachId,
      },
    });
  }, [profileData?.coachId]);

  useEffect(() => {
    if (toastConfig && toastConfig.type === "error") {
      const timer = setTimeout(() => {
        dispatch(resetToastConfig());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [toastConfig, dispatch]);

  const handleChange = (id, e) => {
    const { name, value } = e.target;

    if (name === "phoneNumber") {
      if (!isValidNumberInput(value)) {
        return;
      }
    }

    if (name === "firstName" || name === "lastName") {
      if (!isValidTextInput(value)) {
        return;
      }
    }

    if (name === "note") {
      if (value.length > 255) {
        return;
      }
    }

    if (name === "country") {
      const selectedCountry = countriesArr.find(
        (country) => country.countryId === value
      );
      setForms((prevForms) =>
        prevForms.map((form) =>
          form.id === id
            ? {
                ...form,
                formData: {
                  ...form.formData,
                  country: selectedCountry ? selectedCountry.countryId : value,
                },
                errors: { ...form.errors, country: "" },
              }
            : form
        )
      );
      return;
    }

    setForms((prevForms) =>
      prevForms.map((form) =>
        form.id === id
          ? {
              ...form,
              formData: { ...form.formData, [name]: value },
              errors: { ...form.errors, [name]: "" },
            }
          : form
      )
    );
  };

  const handleAddForm = () => {
    if (forms.length < 5) {
      appInsights.trackEvent({
        name: "Add_new_lead: Add Lead Form",
        properties: {
          currentCount: forms.length,
        },
      });

      setForms([
        ...forms,
        {
          id: Date.now(),
          formData: {
            firstName: "",
            lastName: "",
            email: "",
            phoneNumber: "",
            language: "",
            country: "",
            note: "",
          },
          errors: {
            firstName: "",
            lastName: "",
            email: "",
            phoneNumber: "",
          },
        },
      ]);
    }
  };

  useEffect(() => {
    dispatch(fetchCountries());
  }, [dispatch]);

  const countriesArr = countries.map((country) => ({
    value: country.countryId,
    label: country.countryName,
    countryId: country.countryId,
  }));

  const handleRemoveForm = (id) => {
    appInsights.trackEvent({
      name: "Add_new_lead: Remove Lead Form",
      properties: { formId: id },
    });

    setForms(forms.filter((form) => form.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validatedForms = forms.map(({ formData }) => {
      const { isValid, errors } = validateLeadForm(formData, trans);
      return { formData, isValid, errors };
    });

    const isAllValid = validatedForms.every(({ isValid }) => isValid);

    if (isAllValid) {
      const payload = validatedForms.map(({ formData }) => ({
        ...formData,
        status: "New Lead",
        isLeadFromSite: false,
        createdBy: "",
        updatedBy: "",
      }));

      try {
        const coachId = profileData?.coachId;

        appInsights.trackEvent({
          name: "Add_new_lead: Submit Leads",
          properties: {
            count: payload.length,
            coachId: coachId,
          },
        });

        await dispatch(
          postMultipleLeadsThunk({ coachId, leads: payload })
        ).unwrap();

        appInsights.trackEvent({
          name: "Add_new_lead: Leads Created Successfully",
          properties: { count: payload.length },
        });

        navigate("/leads");
      } catch (error) {
        appInsights.trackException({
          exception: error,
          severityLevel: 2,
          properties: {
            name: "Add_new_lead: Leads Creation Failed",
            error: error?.message,
          },
        });

        dispatch(
          setToastForAddLead({
            type: "error",
            message: trans("LeadPage.leadsCreationFailed"),
            heading: trans("Common.failed"),
          })
        );
      }
    } else {
      appInsights.trackEvent({
        name: "Add_new_lead: Lead Validation Failed",
        properties: {
          formCount: forms.length,
        },
      });

      setForms((prevForms) =>
        prevForms.map((form, index) => ({
          ...form,
          errors: validatedForms[index].errors,
        }))
      );
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="createLeadHeading">
        <div className="cancelButton">
          <Button
            variant="outline-primary"
            size="sm"
            className="cancel-button"
            onClick={() => navigate("/leads")}
          >
            <i className="bi bi-chevron-left"></i> {trans("Common.back")}
          </Button>
          <h4 className="ms-3 mt-2 d-flex justify-content-center align-item-end">
            {trans("LeadPage.addDetails")}
          </h4>
        </div>

        <Button className="saveButton" onClick={handleSubmit}>
          <i className="bi bi-floppy2-fill"></i> {trans("Common.save")}
        </Button>
      </div>

      {forms.map(({ id, formData, errors }, index) => (
        <div key={id} className=" formContainer mb-4">
          <div className="templateWrapper px-4 py-4">
            <h6 className="pb-2 d-flex justify-content-between align-items-center">
              {trans("LeadPage.personalInformation")}
              {index > 0 && (
                <Button
                  variant="outline-danger"
                  onClick={() => handleRemoveForm(id)}
                  size="sm"
                  className="close_button"
                >
                  <i className="bi bi-x-circle"></i> {trans("Common.close")}
                </Button>
              )}
            </h6>

            <LeadForm
              formData={formData}
              errors={errors}
              onChange={(e) => handleChange(id, e)}
              isEditMode={true}
              showLeadInfo={false}
              countryOptions={countriesArr}
              languageOptions={languages}
            />
          </div>
        </div>
      ))}

      <div className="d-flex justify-content-end">
        <Button
          variant="primary"
          className="addButton"
          onClick={handleAddForm}
          disabled={forms.length >= 5}
          style={{
            opacity: forms.length >= 5 ? 0.3 : 1,
            cursor: forms.length >= 5 ? "not-allowed" : "pointer",
          }}
        >
          {trans("LeadPage.addMoreLead")}
        </Button>
      </div>
      {toastConfig && toastConfig.type === "error" && (
        <ToastNotification
          type={toastConfig.type}
          message={toastConfig.message}
          heading={toastConfig.heading}
        />
      )}
    </div>
  );
};

export default AddNewLead;
